import React, { useEffect, useRef, useState } from 'react'

const ZoomableImageViewer = ({
  src,
  alt = 'Screenshot',
  containerClass = ''
}) => {
  const [zoom, setZoom] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [loading, setLoading] = useState(true)
  const containerRef = useRef(null)

  // 1s spinner on every new screenshot so the switch is always perceptible, plus a
  // zoom reset so the next shot doesn't inherit the last one's pan.
  // ponytail: flat timer, not the img's onLoad — the image keeps loading underneath
  // the overlay. If slow screenshots start showing blank after 1s, gate on onLoad.
  useEffect(() => {
    setLoading(true)
    setZoom(1)
    setTranslate({ x: 0, y: 0 })
    const t = setTimeout(() => setLoading(false), 1000)
    return () => clearTimeout(t)
  }, [src])

  useEffect(() => {
    const handleMouseMove = e => {
      if (isDragging) {
        setTranslate({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        })
      }
    }

    const handleMouseUp = () => {
      if (isDragging) setIsDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragStart])

  const handleMouseDown = e => {
    e.preventDefault()
    const rect = containerRef.current.getBoundingClientRect()
    setIsDragging(true)
    setDragStart({
      x: e.clientX - translate.x,
      y: e.clientY - translate.y
    })
  }

  return (
    <div
      ref={containerRef}
      className={`relative ${containerClass}`}
      onMouseDown={handleMouseDown}
      style={{
        cursor: isDragging ? 'grabbing' : zoom > 1 ? 'grab' : 'default'
      }}
    >
      {loading && (
        <div className='absolute inset-0 z-30 flex items-center justify-center bg-white/70'>
          <div className='spinner-border animate-spin inline-block w-12 h-12 border-4 rounded-full text-green-500'></div>
        </div>
      )}

      <div className='sticky top-2 right-2 z-20 flex gap-1 justify-end bg-white/80 p-1 rounded shadow'>
        <button
          className='btn btn-xs btn-default'
          onClick={() => setZoom(z => Math.min(z + 0.1, 3))}
        >
          +
        </button>
        <button
          className='btn btn-xs btn-default'
          onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))}
        >
          -
        </button>
        <button
          className='btn btn-xs btn-warning'
          onClick={() => {
            setZoom(1)
            setTranslate({ x: 0, y: 0 })
          }}
        >
          Reset
        </button>
      </div>

      <img
        src={src}
        alt={alt}
        className='rounded shadow transition-transform duration-200 ease-in-out select-none'
        draggable={false}
        style={{
          transform: `scale(${zoom}) translate(${translate.x / zoom}px, ${
            translate.y / zoom
          }px)`,
          transformOrigin: 'top left',
          userSelect: 'none',
          pointerEvents: 'auto'
        }}
      />
    </div>
  )
}

export default ZoomableImageViewer
